import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import logo from '../assets/logo.png';

interface Props {
  onBack: () => void;
  cycleWeek?: string;
}

export default function SettingsPage({ onBack, cycleWeek }: Props) {
  const { token, logout } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    if (!token) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await api.post('/player/me/delete', { confirm: 'DELETE' }, token);
      if (res?.ok) {
        logout();
      } else {
        setError(res?.error || 'Failed to delete account');
        setDeleting(false);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="dashboard">
        <header className="dashboard-header">
          <div className="dashboard-header-nav">
            <button onClick={onBack} className="btn-back">
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
            {cycleWeek && <div className="week-label">{cycleWeek}</div>}
            <div style={{ width: 70 }} />
          </div>
          <img src={logo} alt="NoWeakWeek" className="dashboard-logo" />
          <h1>Settings</h1>
        </header>

        <div className="settings-content">
          <div className="settings-section">
            <h2><FontAwesomeIcon icon={faExclamationTriangle} /> Danger Zone</h2>
            <p className="settings-description">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <p className="settings-description">
              Your Strava authorization will also be revoked.
            </p>

            {!showDeleteConfirm ? (
              <button className="settings-btn-delete" onClick={() => setShowDeleteConfirm(true)}>
                Delete Account
              </button>
            ) : (
              <div className="settings-delete-confirm">
                <div className="settings-delete-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>Are you sure you want to delete your account?</span>
                </div>
                <p className="settings-delete-detail">This will permanently delete:</p>
                <ul className="settings-delete-list">
                  <li>Your profile and all personal data</li>
                  <li>All activities, workouts, and scoring data</li>
                  <li>Your match history and ELO rating</li>
                  <li>Your Strava connection (access will be revoked)</li>
                </ul>
                <p className="settings-delete-detail" style={{ color: '#ffb63c', fontWeight: 600 }}>
                  ⚠️ If you have an active duel, your opponent will receive an automatic win.
                </p>
                <p className="settings-delete-detail settings-delete-final">
                  This action <strong>cannot be undone</strong>.
                </p>
                {error && <div className="settings-delete-error">{error}</div>}
                <div className="settings-delete-actions">
                  <button
                    className="settings-btn-cancel"
                    onClick={() => { setShowDeleteConfirm(false); setError(null); }}
                    disabled={deleting}
                  >Cancel</button>
                  <button
                    className="settings-btn-confirm-delete"
                    onClick={handleDelete}
                    disabled={deleting}
                  >{deleting ? 'Deleting...' : 'Yes, delete my account'}</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
